import { useEffect, useState } from "react";
import api, { inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, Plus, X, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loading } from "@/components/Loading";

const UNITS = ["piece", "sqft", "1000 pcs", "100 pcs", "kg", "meter", "set"];

export default function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [name, setName] = useState("");
  const [rate, setRate] = useState("");
  const [unit, setUnit] = useState("piece");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/products", { name, default_rate: Number(rate || 0), unit });
      toast.success(`${name} added`);
      setName(""); setRate(""); setUnit("piece");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add product");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, pname) => {
    if (!window.confirm(`Delete ${pname}?`)) return;
    await api.delete(`/products/${id}`);
    toast.success("Product deleted");
    load();
  };

  const saveEdit = async () => {
    if (!editing.name.trim()) return toast.error("Product name required");
    try {
      await api.put(`/products/${editing.id}`, { name: editing.name, default_rate: Number(editing.default_rate || 0), unit: editing.unit });
      toast.success("Product updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="products-tab">
      <div className="bg-card border border-border rounded-xl shadow-sm p-5">
        <h3 className="font-heading font-semibold flex items-center gap-2">
          <Plus className="h-4 w-4 text-cyan-600" /> New Product
        </h3>
        <form onSubmit={add} className="mt-4 flex flex-wrap gap-3">
          <Input placeholder="Product name *" value={name} onChange={(e) => setName(e.target.value)} required data-testid="product-name-input" className="flex-1 min-w-44" />
          <Input placeholder="Default Rate" type="number" min="0" step="any" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="product-rate-input" className="w-32" />
          <Select value={unit} onValueChange={setUnit}>
            <SelectTrigger className="w-32" data-testid="product-unit-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button type="submit" disabled={busy} data-testid="product-add-btn" className="bg-cyan-600 hover:bg-cyan-700 text-white">
            Add Product
          </Button>
        </form>
      </div>

      <div className="bg-card border border-border rounded-xl shadow-sm p-5">
        <h3 className="font-heading font-semibold flex items-center gap-2 mb-4">
          <Package className="h-4 w-4 text-slate-400" /> Product Catalog ({products.length})
        </h3>
        {loading ? (
          <Loading text="Loading products..." />
        ) : products.length === 0 ? (
          <p className="text-muted-foreground text-sm" data-testid="products-empty">No products yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="product-catalog-grid">
            {products.map((p) => (
              <div key={p.id} className="flex items-center justify-between border border-border rounded-lg px-3.5 py-3 hover:shadow-sm transition-shadow" data-testid={`product-chip-item-${p.id.slice(0, 8)}`}>
                <div>
                  <p className="font-medium text-sm">{p.name}</p>
                  <p className="text-xs text-muted-foreground">{inr(p.default_rate)} / {p.unit}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setEditing({ ...p })} data-testid={`product-edit-btn-${p.id.slice(0, 8)}`} className="text-slate-400 hover:text-cyan-600 transition-colors" title="Edit">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => remove(p.id, p.name)} data-testid={`product-delete-btn-${p.id.slice(0, 8)}`} className="text-slate-300 hover:text-red-500 transition-colors" title="Delete">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-sm" data-testid="product-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Product</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Product name *" data-testid="product-edit-name-input" />
              <div className="flex gap-2">
                <Input type="number" min="0" step="any" value={editing.default_rate} onChange={(e) => setEditing({ ...editing, default_rate: e.target.value })} placeholder="Rate" data-testid="product-edit-rate-input" />
                <Select value={editing.unit} onValueChange={(v) => setEditing({ ...editing, unit: v })}>
                  <SelectTrigger className="w-32" data-testid="product-edit-unit-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={saveEdit} data-testid="product-edit-save-btn" className="bg-cyan-600 hover:bg-cyan-700 text-white">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
